import React, { useCallback, useEffect, useState } from "react";
import { Badge, Card, CardBody, CardHeader, CardTitle, Spinner, Table } from "reactstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";

import { apiFetch, API } from "../../services/api";
import { PushHealthCard } from "../../components/security/PushHealthCard";
import { apiErrorMessage, formatDateTime } from "../../helpers/security";
import {
    ALARM_VIEWS,
    ERROR_WINDOWS,
    MUTE_DURATIONS,
    countByState,
    filterAlarms,
    groupByFamily,
    isMuted,
    since,
    stateColor,
    stateLabel,
} from "../../helpers/alarms";

// Page « Alarmes » : état des alarmes CloudWatch (notifications SNS
// « alerting »), erreurs récentes de la lambda, note partagée, sourdine avec
// réactivation automatique, relance d'un robot planifié et réimport d'un flux.

function call(path, options) {
    return apiFetch(API.back + path, { market: false, ...options }).then((res) => {
        if (!res.ok) {
            return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
        }
        return res.json();
    });
}

function post(path, body, method = "POST") {
    return call(path, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
}

export function Alarms() {
    const [alarms, setAlarms] = useState(null);
    const [error, setError] = useState(null);
    const [view, setView] = useState("alert");
    const [query, setQuery] = useState("");
    const [selected, setSelected] = useState(null);

    const load = useCallback(() => {
        call("/alarms", { method: "GET" })
            .then((data) => {
                setAlarms(Array.isArray(data) ? data : []);
                setError(null);
            })
            .catch((err) => {
                setError("Alarmes indisponibles (" + err.message + ").");
                setAlarms([]);
            });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = countByState(alarms);
    const groups = groupByFamily(filterAlarms(alarms, view, query));

    return (
        <div>
            <PushHealthCard />
            <Card className="flex-fill w-100">
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-start flex-wrap">
                        <div>
                            <CardTitle tag="h5" className="mb-1">
                                Alarmes
                            </CardTitle>
                            <div className="text-muted small">
                                {alarms
                                    ? `${counts.ALARM} en alerte · ${counts.muted} en sourdine · ${counts.INSUFFICIENT_DATA} sans données · ${counts.OK} OK`
                                    : "Chargement…"}
                            </div>
                        </div>
                        <div className="d-flex align-items-center flex-wrap">
                            {ALARM_VIEWS.map((v) => (
                                <Button
                                    key={v.value}
                                    size="sm"
                                    className="mr-1"
                                    variant={view === v.value ? "dark" : "outline-secondary"}
                                    onClick={() => setView(v.value)}
                                >
                                    {v.label}
                                </Button>
                            ))}
                            <Form.Control
                                size="sm"
                                className="ml-2"
                                style={{ width: 220 }}
                                placeholder="Rechercher (lambda, note…)"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <Button size="sm" variant="outline-secondary" className="ml-2" onClick={load}>
                                Rafraîchir
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardBody>
                    {error ? <div className="alert alert-danger">{error}</div> : null}
                    {!alarms ? (
                        <div className="d-flex align-items-center">
                            <Spinner color="primary" size="sm" />
                            <span className="text-muted ml-2">Chargement…</span>
                        </div>
                    ) : null}
                    {alarms && groups.length === 0 && !error ? (
                        <p className="text-muted mb-0">
                            {view === "alert" ? "Aucune alarme en alerte." : "Aucune alarme."}
                        </p>
                    ) : null}
                    {groups.map((group) => (
                        <div key={group.family} className="mb-4">
                            <h6 className="text-uppercase text-muted small mb-2">
                                {group.family} ({group.alarms.length})
                            </h6>
                            <Table responsive size="sm" className="mb-0">
                                <tbody>
                                    {group.alarms.map((a) => (
                                        <tr key={a.name} style={{ cursor: "pointer" }} onClick={() => setSelected(a.name)}>
                                            <td style={{ width: 110 }}>
                                                <Badge color={stateColor(a.state)}>{stateLabel(a.state)}</Badge>
                                            </td>
                                            <td>
                                                <div>{a.name}</div>
                                                {a.note ? <div className="text-muted small">📝 {a.note}</div> : null}
                                            </td>
                                            <td className="text-muted small" style={{ width: 130 }}>
                                                {since(a.stateUpdatedAt)}
                                            </td>
                                            <td style={{ width: 200 }}>
                                                {isMuted(a) ? (
                                                    <Badge color="warning">
                                                        {a.mutedUntil
                                                            ? "Sourdine jusqu'au " + formatDateTime(a.mutedUntil)
                                                            : "Notifications coupées"}
                                                    </Badge>
                                                ) : null}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ))}
                </CardBody>
            </Card>
            {selected ? (
                <AlarmDetail
                    name={selected}
                    onClose={() => setSelected(null)}
                    onChanged={load}
                />
            ) : null}
        </div>
    );
}

function AlarmDetail({ name, onClose, onChanged }) {
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);
    const [hours, setHours] = useState(24);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(null);
    const [message, setMessage] = useState(null);
    const [muteHours, setMuteHours] = useState(24);

    const load = useCallback(() => {
        setDetail(null);
        call("/alarm?name=" + encodeURIComponent(name) + "&hours=" + hours, { method: "GET" })
            .then((d) => {
                setDetail(d);
                setNote(d.note || "");
                setError(null);
            })
            .catch((err) => setError(err.message));
    }, [name, hours]);

    useEffect(() => {
        load();
    }, [load]);

    const act = (label, promise, success) => {
        setBusy(label);
        setMessage(null);
        promise
            .then(() => {
                setBusy(null);
                setMessage({ ok: true, text: success });
                load();
                onChanged();
            })
            .catch((err) => {
                setBusy(null);
                setMessage({ ok: false, text: err.message });
            });
    };

    const reimport = () =>
        act(
            "reimport",
            apiFetch(`${API.products}/importer-run`, {
                method: "POST",
                market: false,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key: detail.importerKey, force: true }),
            }).then((res) =>
                res.ok ? null : res.text().then((b) => Promise.reject(new Error(apiErrorMessage(res.status, b))))
            ),
            "Réimport lancé (le flux est intégralement retraité)."
        );

    return (
        <Modal show onHide={onClose} size="xl" scrollable>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: 18, wordBreak: "break-all" }}>{name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error ? <div className="alert alert-danger">{error}</div> : null}
                {!detail && !error ? <Spinner color="primary" size="sm" /> : null}
                {detail ? (
                    <div>
                        <div className="mb-3">
                            <Badge color={stateColor(detail.state)} className="mr-2">
                                {stateLabel(detail.state)}
                            </Badge>
                            <span className="text-muted small">
                                {since(detail.stateUpdatedAt)} · {detail.family}
                                {detail.function ? " · lambda " + detail.function : ""}
                            </span>
                            <div className="small mt-1">{detail.reason}</div>
                            {detail.description ? <div className="small text-muted">{detail.description}</div> : null}
                        </div>

                        {message ? (
                            <div className={"alert " + (message.ok ? "alert-success" : "alert-danger")}>{message.text}</div>
                        ) : null}

                        <div className="d-flex flex-wrap align-items-center mb-4">
                            {isMuted(detail) ? (
                                <>
                                    <Badge color="warning" className="mr-2">
                                        {detail.mutedUntil
                                            ? "Sourdine jusqu'au " + formatDateTime(detail.mutedUntil) + (detail.mutedBy ? " (" + detail.mutedBy + ")" : "")
                                            : "Notifications coupées hors pikachu"}
                                    </Badge>
                                    <Button
                                        size="sm"
                                        variant="outline-dark"
                                        className="mr-3"
                                        disabled={busy !== null}
                                        onClick={() => act("unmute", post("/alarm-unmute", { name }), "Notifications réactivées.")}
                                    >
                                        Réactiver les notifications
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        style={{ width: 120 }}
                                        value={muteHours}
                                        onChange={(e) => setMuteHours(Number(e.target.value))}
                                    >
                                        {MUTE_DURATIONS.map((d) => (
                                            <option key={d.hours} value={d.hours}>
                                                {d.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                    <Button
                                        size="sm"
                                        variant="outline-dark"
                                        className="ml-1 mr-3"
                                        disabled={busy !== null}
                                        onClick={() =>
                                            act("mute", post("/alarm-mute", { name, hours: muteHours }), "Notifications coupées, réactivation automatique programmée.")
                                        }
                                    >
                                        Mettre en sourdine
                                    </Button>
                                </>
                            )}
                            {detail.rerun ? (
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="mr-2"
                                    disabled={busy !== null}
                                    title={"Planification " + detail.rerun.source + " : " + detail.rerun.schedule}
                                    onClick={() => act("rerun", post("/alarm-rerun", { name }), "Lambda relancée (exécution en arrière-plan).")}
                                >
                                    Relancer la lambda
                                </Button>
                            ) : null}
                            {detail.importerKey ? (
                                <Button size="sm" variant="outline-primary" disabled={busy !== null} onClick={reimport}>
                                    Forcer le réimport ({detail.importerLabel})
                                </Button>
                            ) : null}
                            {busy ? <Spinner size="sm" color="primary" className="ml-2" /> : null}
                        </div>

                        <h6>Note</h6>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={note}
                            placeholder="Ex. : erreurs 500 passagères de Google, se répare toute seule."
                            onChange={(e) => setNote(e.target.value)}
                        />
                        <div className="d-flex align-items-center mt-1 mb-4">
                            <Button
                                size="sm"
                                variant="outline-secondary"
                                disabled={busy !== null || note === (detail.note || "")}
                                onClick={() => act("note", post("/alarm-note", { name, note }, "PUT"), "Note enregistrée.")}
                            >
                                Enregistrer la note
                            </Button>
                            {detail.noteUpdatedBy ? (
                                <span className="text-muted small ml-2">
                                    par {detail.noteUpdatedBy}, le {formatDateTime(detail.noteUpdatedAt)}
                                </span>
                            ) : null}
                        </div>

                        {detail.function ? (
                            <>
                                <div className="d-flex align-items-center mb-2">
                                    <h6 className="mb-0 mr-3">Erreurs de la lambda</h6>
                                    {ERROR_WINDOWS.map((w) => (
                                        <Button
                                            key={w.hours}
                                            size="sm"
                                            className="mr-1"
                                            variant={hours === w.hours ? "dark" : "outline-secondary"}
                                            onClick={() => setHours(w.hours)}
                                        >
                                            {w.label}
                                        </Button>
                                    ))}
                                </div>
                                {detail.errorsUnavailable ? (
                                    <div className="alert alert-warning">{detail.errorsUnavailable}</div>
                                ) : null}
                                {detail.errors.length === 0 && !detail.errorsUnavailable ? (
                                    <p className="text-muted small">Aucune erreur journalisée sur la période.</p>
                                ) : null}
                                {detail.errorsTruncated ? (
                                    <p className="text-muted small">Beaucoup d'erreurs : seules les 600 premières sont analysées.</p>
                                ) : null}
                                {detail.errors.length > 0 ? (
                                    <Table size="sm" responsive className="mb-4">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 60 }}>Nb</th>
                                                <th>Message</th>
                                                <th style={{ width: 150 }}>Dernière</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.errors.map((g, i) => (
                                                <tr key={i}>
                                                    <td>{g.count}</td>
                                                    <td>
                                                        <div style={{ wordBreak: "break-word" }}>{g.sample}</div>
                                                        {g.examples && g.examples.length > 1 ? (
                                                            <div className="text-muted small">Valeurs : {g.examples.join(", ")}</div>
                                                        ) : null}
                                                    </td>
                                                    <td className="text-muted small">{formatDateTime(g.last)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : null}
                            </>
                        ) : (
                            <p className="text-muted small mb-4">Cette alarme ne porte pas sur une lambda (pas de journal d'erreurs).</p>
                        )}

                        <h6>Historique (7 jours)</h6>
                        {detail.history.length === 0 ? <p className="text-muted small">Aucun changement d'état.</p> : null}
                        <ul className="small mb-4">
                            {detail.history.map((h, i) => (
                                <li key={i}>
                                    <span className="text-muted">{formatDateTime(h.time)}</span> — {h.summary}
                                </li>
                            ))}
                        </ul>

                        <h6>Journal des actions</h6>
                        {detail.journal.length === 0 ? <p className="text-muted small mb-0">Aucune action.</p> : null}
                        <ul className="small mb-0">
                            {detail.journal.map((j, i) => (
                                <li key={i}>
                                    <span className="text-muted">{formatDateTime(j.time)}</span> — {j.user} : {j.action}
                                    {j.detail ? " (" + j.detail + ")" : ""}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </Modal.Body>
        </Modal>
    );
}
